/** @jsxImportSource @emotion/react */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  createContext,
  useRef
} from 'react';
import css from '@emotion/css';
import { Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import {
  Collapse,
  Fade,
  IconButton,
  InputBase as MuiInput,
  Menu,
  MenuItem as MuiMenuItem,
  OutlinedInput,
  Button as MuiButton,
  Select as MuiSelect,
  Tooltip,
  Checkbox
} from '@material-ui/core';
// import SettingsIcon from '@material-ui/icons/MoreVert';
import { cloneDeep } from 'lodash';
import DeleteIcon from '@material-ui/icons/DeleteForeverOutlined';
import EditIcon from '@material-ui/icons/Edit';
import CopyIcon from '../../icons/Copy';
import ArchiveIcon from '@material-ui/icons/Archive';
import EmojiInserterEngine from '../emoji-inserter/EmojiInserterEngine';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import FlagIcon from '@material-ui/icons/FlagOutlined';
import classNames from 'classnames';
// import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { compareTypeMap } from '../conditional-logic/compareTypeMap';

// import FillParent from '../../components/layouts/FillParent';
import { TextString } from '../../components/Text';
import {
  DELETE_FIELD,
  DELETE_FIELDS,
  UPDATE_FIELD,
  UPDATE_FORM,
  DUPLICATE_FIELD,
  ADD_FIELD
} from '../../store/modules/state/formBuilder';
import { rgba, mix } from 'polished';

import { makeStyles } from '@material-ui/core/styles';

import AddCircleIcon from '@material-ui/icons/AddCircleOutline';
import OptionsBuilder from './OptionsBuilder';

import { fieldTypes, fieldTypesWithHeaders } from './fieldTypes';

import useFetchWithCallbacks from '../../hooks/useFetchWithCallbacks';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';

import useTranslations from '../../hooks/useTranslations';
import debounce from 'lodash.debounce';
import GeneralDialog from '../GeneralDialog';
import RemoveOptionDialog from '../GeneralDialog';

import FieldOptionsDrawer from './engine/FieldOptionsDrawerEngine';
import CurrencyConversionSettingsPanelEngine from './engine/CurrencyConversionSettingsPanelEngine';
import LookupSettingsPanelEngine from './engine/LookupSettingsPanelEngine';
import ScoreSettingsPanelEngine from './engine/ScoreSettingsPanelEngine';

import generateId from '../../utils/generateId';
import { isNull } from 'lodash';
import CharacterCounter from '../CharacterCounter';
import Animation from '../../components/Animation';
import { FormBuilderEditContext } from './FormBuilderEdit';
import { ContactlessOutlined } from '@material-ui/icons';
import {
  getAllFieldsBySection,
  getAllIdsOfSection,
  getDuplicateSection,
  getDuplicateRules,
  mapExistingFieldIdsToNewIds
} from '../../utils/formUtils';
import CantDeleteFieldDialogEngine from '../../components/engine/CantDeleteFieldDialogEngine';

export const FormBuilderFieldFormContext = createContext({});
const DuplicateIcon = styled(CopyIcon)`
  transform: scaleY(-1) rotate(-90deg);
`;

const compatibleTypesMap = {
  text: ['text', 'long_text', 'link'],
  long_text: ['text', 'long_text'],
  link: ['text'],
  number: ['number', 'currency'],
  currency: ['number', 'currency'],
  dropdown: ['dropdown', 'yes_no'],
  multiple_choice: [],
  yes_no: ['dropdown', 'yes_no'],
  phone: [],
  date: [],
  email_address: [],
  file_upload: [],
  section_break: [],
  instructional_text: [],
  confirmation: [],
  currency_conversion: [],
  lookup: [],
  user_lookup: [],
  credas: [],
  credas_out_of_scope: [],
  thirdfort: [],
  worldcheck: [],
  worldcheck_org: [],
  score: []
};

const neverChangeTypes = [
  'credas',
  'credas_out_of_scope',
  'currency_conversion',
  'lookup',
  'user_lookup',
  'thirdfort',
  'worldcheck',
  'worldcheck_org'
];

export const MenuH3 = styled.h3`
  margin: 16px;
  color: #000;
  outline: none !important;
`;

export const MenuH2 = styled.h2`
  margin: 16px;
  color: #000;
  outline: none !important;
`;

export const Select = props => (
  <MuiSelect
    variant="outlined"
    inputProps={{ placeholder: 'Please choose an option.' }}
    // input={<OutlinedInput placeholder="Please choose an option." />}
    {...props}
  />
);

export const StyledFieldTypeMenuItem = styled(MuiMenuItem)`
  &&&& {
    padding: 8px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: start;
    min-width: 360px;
    max-width: 95vw;

    @media (max-width: 400px) {
      width: 95vw;
      min-width: none;
    }

    ${({ selected, theme }) =>
      selected
        ? `
          background-color: ${rgba(theme.palette.primary.main, 0.75)}};`
        : `
          &:hover {
            background-color: ${rgba(theme.palette.primary.main, 0.1)}};
          }`}
  }
`;

const EmojiButtonContainer = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  && button {
    color #000 !important;
  }
`;

const GUTTER = 16;

const FBFF__FieldTypeIconBox = styled.div`
  vertical-align: middle;
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ color, disabled, theme }) =>
    disabled ? theme.palette.grey[300] : color};
  font-weight: 600;
  text-shadow: none;
  user-select: none;
  cursor: default;
  background-color: ${({ color, disabled, theme }) =>
    disabled
      ? rgba(theme.palette.grey[300], 0.25)
      : rgba(mix(0.7, color, '#fff'), 0.25)};
`;

export const FieldTypeIcon = ({ fieldType, ...props }) => (
  <FBFF__FieldTypeIconBox color={fieldTypes[fieldType].color} {...props}>
    {fieldTypes[fieldType].icon}
  </FBFF__FieldTypeIconBox>
);

const ActionIconButton = styled(IconButton)`
  && {
    color: #bfc5d1;
    padding: 8px;

    svg {
      font-size: 1.25rem;
    }

    &:hover {
      background-color: ${({ theme }) => rgba(theme.palette.primary.main, 0.1)};
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const useStyles = makeStyles(theme => ({
  placementBottom: {
    margin: '8px 0'
  }
}));

const ActionButton = React.forwardRef((props, ref) => {
  const { label, icon, onClick, ...rest } = props;
  const classes = useStyles();

  return (
    <Tooltip
      title={label}
      classes={{
        tooltipPlacementBottom: classes.placementBottom
      }}
    >
      <span ref={ref} className="FBFF__action-iconbutton">
        <ActionIconButton
          onClick={onClick}
          color="default"
          aria-label={label}
          {...rest}
        >
          {icon}
        </ActionIconButton>
      </span>
    </Tooltip>
  );
});

const Input = styled(MuiInput)`
  && {
    width: 100%;
    border: none;
    outline: none;
    min-height: 48px;
    padding: 0;
    background: transparent;

    &:before,
    &:after {
      content: none;
    }

    textarea {
      max-height: 400px;
      overflow: auto !important;

      font-family: ${({ theme }) => theme.typography.fontFamily};
      font-size: 14px;
      line-height: 1.5;

      ${({ isH2 }) =>
        isH2 &&
        `
          font-weight: 600;
          font-size: 20px;
          line-height: 24px;
      `}
    }
  }

  && + && {
    margin-top: 32px;
  }
`;

export const AddButton = styled(MuiButton)`
  && {
    text-align: left;
    padding: 16px 24px;
    border-radius: 0;
    width: 100%;
    justify-content: start;
    text-transform: none;
    font-weight: 200;
  }
`;

const InlineButton = styled(MuiButton)`
  && {
    width: 100%;
    height: 24px;
    margin: 4px 0;
    padding: 0;

    text-align: center;
    background: transparent;
    text-transform: none;

    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 5;

    svg {
      font-size: 16px;
    }

    [class*='MuiButton-label'] {
      font-size: 13px;
    }
  }
`;

const FieldCornerIconSelect = styled(MuiSelect)`
  && {
    position: absolute;
    left: ${GUTTER}px;
    top: ${GUTTER}px;
  }

  [class*='MuiInputBase-input'] {
    padding: 0 !important;
  }

  [class*='MuiSelect-icon'] {
    display: none;
  }
`;

const CharacterCounterDiv = styled.div`
  bottom: 13px;
  left: 1rem;
  position: absolute;
`;

const FieldCheckboxContainer = styled.div`
  display: block;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s;
  position: absolute;
  top: 0.1rem;
  right: 0.1rem;
`;

const StyledCheckbox = styled(Checkbox)`
  && svg {
    color: #1e202d;
    font-size: 21px !important;
    opacity: 0.3;
  }
  &&.Mui-checked svg {
    color: #4c84ff;
    opacity: 1;
  }
`;

const updateOptions = (fieldOptions, calculationMapping) => {
  for (let i = 0; i < fieldOptions.length; i++) {
    if (fieldOptions[i] !== calculationMapping[i].optionName) {
      calculationMapping[i].optionName = fieldOptions[i];
    }
  }
  return calculationMapping;
};

const updateScoringMap = (fieldOptions, calculationMapping) => {
  let scoringValues = [];
  if (calculationMapping && calculationMapping.length) {
    if (calculationMapping.length === fieldOptions.length) {
      scoringValues = updateOptions(fieldOptions, calculationMapping);
    }
    //if the user added a new option to the field and did not yet add a scoring value
    else {
      for (
        let i = 0, j = 0;
        i < fieldOptions.length && j < fieldOptions.length;
        i++, j++
      ) {
        if (
          calculationMapping[j] &&
          calculationMapping[j].optionName === fieldOptions[i]
        ) {
          scoringValues.push({
            optionName: fieldOptions.at(i),
            value: calculationMapping[j].value
          });
        } else if (
          !calculationMapping[j] ||
          calculationMapping[j].optionName !== fieldOptions[i]
        ) {
          scoringValues.push({
            optionName: fieldOptions.at(i),
            value: 0
          });
          j--;
        }
      }
    }
  }
  // if there are no current values in calculationMapping of the field
  else {
    for (let i = 0; i < fieldOptions.length; i++) {
      scoringValues.push({
        optionName: fieldOptions.at(i),
        value: 0
      });
    }
  }
  return scoringValues;
};

export const AddQuestionButton = React.memo(
  ({
    activeIntegrations = {},
    activeLicenses = {},
    autosave,
    after = null,
    compact,
    ButtonComponent = AddButton,
    color = 'primary',
    sharingType,
    ...props
  }) => {
    const buttonRef = React.useRef();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleClose = () => setAnchorEl(null);

    const filteredFieldTypesWithHeaders = cloneDeep(fieldTypesWithHeaders);

    if (sharingType === 'anonymous') {
      delete filteredFieldTypesWithHeaders.credas;
      delete filteredFieldTypesWithHeaders.credas_out_of_scope;
      delete filteredFieldTypesWithHeaders.user_lookup;
      delete filteredFieldTypesWithHeaders.thirdfort;
      delete filteredFieldTypesWithHeaders.worldcheck;
      delete filteredFieldTypesWithHeaders.worldcheck_org;
    }

    if (!activeIntegrations || !activeIntegrations.credas) {
      delete filteredFieldTypesWithHeaders.credas;
      delete filteredFieldTypesWithHeaders.credas_out_of_scope;
    }

    if (!activeIntegrations || !activeIntegrations.thirdfort) {
      delete filteredFieldTypesWithHeaders.thirdfort;
    }

    if (!activeIntegrations || !activeIntegrations.worldcheck) {
      delete filteredFieldTypesWithHeaders.worldcheck;
      delete filteredFieldTypesWithHeaders.worldcheck_org;
    }

    if (!activeLicenses || !activeLicenses.currencyconversion) {
      delete filteredFieldTypesWithHeaders.currency_conversion;
    }

    return (
      <>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          style={{ maxHeight: '70vh' }}
          data-testid={`formbuilder-add-field-menu`}
        >
          {Object.keys(filteredFieldTypesWithHeaders).map((ft, i) => {
            if (fieldTypesWithHeaders[ft].type === 'section_header') {
              return (
                <MenuH3 tabIndex={null} key={i}>
                  <TextString content={fieldTypesWithHeaders[ft].name} />
                </MenuH3>
              );
            } else {
              return (
                <StyledFieldTypeMenuItem
                  key={ft}
                  value={ft}
                  data-testid={`formbuilder-add-field-type-${ft}`}
                  onClick={() => {
                    autosave({
                      type: ADD_FIELD,
                      payload: {
                        after,
                        field: { type: fieldTypes[ft].type, uiType: ft }
                      }
                    });
                    handleClose();
                  }}
                >
                  <FieldTypeIcon fieldType={ft} />

                  <TextString content={fieldTypes[ft].name} />
                </StyledFieldTypeMenuItem>
              );
            }
          })}
        </Menu>
        <ButtonComponent
          buttonRef={buttonRef}
          data-testid={`formbuilder-add-new-question`}
          color={color}
          onClick={() => setAnchorEl(buttonRef.current)}
          {...props}
        >
          <AddCircleIcon
            style={
              compact
                ? { marginRight: 6, marginLeft: '-1ch' }
                : { margin: '0 0.5ch 0 0' }
            }
          />{' '}
          {compact ? 'Add question' : 'Add a new question'}
        </ButtonComponent>
      </>
    );
  }
);

export default React.memo(function FormBuilderFieldForm({
  // spread ...field props
  description: fieldDescription,
  internalId,
  options,
  required,
  title,
  type,
  uiType: fieldUiType,
  defaultCountry,
  position,
  isAdminOnly,
  readOnly,
  lookupInfo = {},
  field,
  provided,
  snapshot = {},
  resizeFromItem,

  // other props
  index,
  access,
  autosave,
  formId,
  rules,
  setRules,
  reminders,
  setReminders,
  isNew,
  hasEntries,
  isLast,
  archived: wasArchived,
  inUseByIntegration,
  deletedField,
  setDeletedField,
  anyFieldSelected
}) {
  const {
    activeIntegrations,
    activeLicenses,
    allFieldsWithSubFields,
    automations,
    fields,
    allLookupDependencies,
    adminFields,
    sharingType,
    multiSelected,
    changeMultiSelected
  } = useContext(FormBuilderEditContext);
  const [isSettingDrawerOpen, setIsSettingDrawerOpen] = useState(false);
  const context = React.useContext(FormBuilderEditContext);

  // To delete a field, set this value to true, which triggers a collapse transition, which triggers the delete action.
  // set to 'section' to delete a whole section
  const [archiveMe, setArchiveMe] = useState(false);
  const [deleteMe, setDeleteMe] = useState(false);

  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteSectionDialog, setShowDeleteSectionDialog] = useState(false);
  const [showDeleteSectionConfirmation, setShowDeleteSectionConfirmation] =
    useState(false);
  const [showCantDeleteFieldDialog, setShowCantDeleteFieldDialog] =
    useState(false);
  const [fieldIdThatCantBeDeleted, setFieldIdThatCantBeDeleted] =
    useState(null);
  const [dependencyData, setDependencyData] = useState({});
  const [warnDeletingFieldUsedInReport, setWarnDeletingFieldUsedInReport] =
    useState(false);
  const [showEmojiDialog, setShowEmojiDialog] = useState(false);
  const [showLookupDialog, setShowLookupDialog] = useState(false);
  const [showCurrencyConversionDialog, setShowCurrencyConversionDialog] =
    useState(false);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [ShowRemoveOptionDialog, setShowRemoveOptionDialog] = useState(false);
  const [showDuplicateSectionDialog, setShowDuplicateSectionDialog] =
    useState(false);

  // description is also stored locally so if it is toggled back on, we remember the local value
  const [description, setDescription] = useState(fieldDescription || '');

  // hover and focus states
  let [isHovered, setIsHovered] = useState(false);
  isHovered = isHovered && !snapshot.isDragging;
  const [hasFocus, setHasFocus] = useState(false);
  const [onceHovered, setOnceHovered] = useState(false);

  // settings menu
  const [archived, setArchived] = useState(wasArchived);
  // local state so value is remembered

  const [warningDisplayedFor, setWarningDisplayedFor] = useState([]);

  const [fieldSettingsTab, setFieldSettingsTab] = useState(null);
  const [fieldCornerIconMenuOpen, setFieldCornerIconMenuOpen] = useState(false);

  const [canEdit, setCanEdit] = useState(false);

  const [isDrawerMounted, setIsDrawerMounted] = useState(false);
  const [rulesToDelete, setRulesToDelete] = useState([]);

  const titleRef = React.useRef(null);

  const emojiTargetRef = React.useRef(null);

  const inUseByRule = rules.some(({ condition = '' }) =>
    condition.includes(internalId)
  );
  const [dontUpdateValue, setDontUpdateValue] = useState(inUseByRule);

  const thisFieldsSubFields = allFieldsWithSubFields.filter(
    one => one.lookupInfo?.subFieldInfo?.parentFieldId === internalId
  );

  const thisFieldsSubFieldIds = thisFieldsSubFields.map(one => one.internalId);

  const inUseInVisibilityLogic = rules.some(rule => {
    if (
      rule.action &&
      (rule.action.actionType === 'show' || rule.action.actionType === 'hide')
    ) {
      if (rule.condition.includes(internalId)) {
        return true;
      }
      return thisFieldsSubFieldIds.some(subFieldId =>
        rule.condition.includes(subFieldId)
      );
    }
    return false;
  });

  const inUseInFlaggingLogic = rules.some(rule => {
    if (rule.action && rule.action.actionType === 'flag') {
      if (rule.condition.includes(internalId)) {
        return true;
      }
      return thisFieldsSubFieldIds.some(subFieldId =>
        rule.condition.includes(subFieldId)
      );
    }
    return false;
  });

  // uiType from form import may not be defined
  const uiType = fieldUiType || (type in fieldTypes ? type : 'text');

  const fetchAPI = useFetchWithCallbacks();

  const counter = useRef(0);
  const throttledAutoSave = payload => {
    counter.current += 1;

    setTimeout(() => {
      counter.current -= 1;
      if (counter.current === 0) {
        autosaveField(UPDATE_FIELD, {
          field: payload
        });
      }
    }, 1000);
  };

  useEffect(() => {
    if (!lookupInfo || !lookupInfo.entityId) {
      setCanEdit(true);
      return;
    }
    fetchAPI(
      `/forms/v1/forms?query=[[{"field":"'id'","op":"=","value":"'${lookupInfo.entityId}'"}]]`
    ).then(res => {
      let fields = [],
        matchingFields = [],
        results = [];
      if (!res || !res.results || !res.results[0] || !res.results[0].fields) {
        setCanEdit(false);
        return;
      }
      setCanEdit(true);
    });
  }, [lookupInfo]);

  const autosaveField = useCallback(
    debounce(
      (t, payload = {}, callback) => {
        autosave({
          type: t,
          payload: {
            fieldId: internalId,
            ...payload
          },
          callback
        });

        // if (resizeFromItem && !snapshot.isDragging) {
        //   resizeFromItem(index);
        // }
      },
      50,
      { leading: true, trailing: true }
    ),
    [autosave, internalId]
  );

  const autosaveSubfield = useCallback(
    debounce(
      (t, payload = {}, callback) => {
        autosave({
          type: t,
          payload: {
            fieldId: internalId,
            ...payload
          },
          callback
        });

        // if (resizeFromItem && !snapshot.isDragging) {
        //   resizeFromItem(index);
        // }
      },
      50,
      { leading: true, trailing: true }
    ),
    [autosave, internalId]
  );

  React.useEffect(() => {
    if (resizeFromItem && !snapshot.isDragging) {
      resizeFromItem(index);
    }
  });

  useEffect(() => {
    if (!isSettingDrawerOpen) {
      setTimeout(() => setIsDrawerMounted(false), 500);
    } else {
      setIsDrawerMounted(true);
    }
  }, [isSettingDrawerOpen]);

  const { openSnackbar } = React.useContext(FetchCallbacksContext);

  const t = useTranslations();

  const archiveField = useCallback(() => {
    autosaveField(UPDATE_FIELD, {
      field: { archived: true }
    });

    let rules2DeleteFromState = [];

    // delete rules on server
    rules.forEach(r => {
      if (r.fieldId === internalId) {
        rules2DeleteFromState.push(r.id);
        fetchAPI(`/forms/v1/rules/${r.id}`, { method: 'DELETE' });
      }
    });

    // delete rules in state
    rules.forEach(rule => {
      if (rule.condition.includes(internalId)) {
        rule.condition = excludeInvalidRules(rule.condition, internalId);
        if (!JSON.parse(rule.condition).length) {
          if (!rules2DeleteFromState.includes(rule.id)) {
            rules2DeleteFromState.push(rule.id);
            fetchAPI(`/forms/v1/rules/${rule.id}`, { method: 'DELETE' });
          }
        } else {
          fetchAPI(`/forms/v1/rules/${rule.id}`, {
            method: 'PUT',
            body: rule
          });
        }
      }
    });

    let newRules = rules.filter(r => !rules2DeleteFromState.includes(r.id));

    setRules(newRules);
    setArchived(true);
    openSnackbar('success', t('snackbars.archive_field.success'));
  }, [rules, setRules, autosaveField, openSnackbar, t, internalId, fetchAPI]);

  const calculateRelatedRules = useCallback(() => {
    let rules2DeleteFromState = [];
    const arrayOfNames = [];
    rules.forEach(rule => {
      if (rule.condition.includes(internalId)) {
        if (rule.action.actionType === 'description') {
          rules2DeleteFromState.push(rule.fieldId);
        }
        if (JSON.parse(rule.condition).length) {
          if (!rules2DeleteFromState.includes(rule.id)) {
            rules2DeleteFromState.push(rule.fieldId);
          }
        }
      }
    });
    for (let ruleFieldId of rules2DeleteFromState) {
      for (let field of allFieldsWithSubFields) {
        if (field.internalId === ruleFieldId) {
          let title = field.title ? field.title : `untitled ${field.uiType}`;
          if (!arrayOfNames.includes(title)) {
            arrayOfNames.push(title);
          }
        }
      }
    }
    setRulesToDelete(arrayOfNames);
    return arrayOfNames;
  }, [rules, allFieldsWithSubFields, internalId]);

  const getAllFieldIdsInSection = useCallback(
    fieldIdForSection => {
      const allFieldsBySection = getAllFieldsBySection(fields, adminFields);
      return (
        allFieldsBySection[fieldIdForSection].map(one => one.internalId) || []
      );
    },
    [fields, adminFields]
  );

  const calculateDependentScoringFields = useCallback(
    fieldId => {
      const allFields = [...fields, ...adminFields];
      const scoringFields = allFields.filter(one => one.uiType === 'score');
      let results = [];
      scoringFields.forEach(one => {
        const formulaString = one?.calculationInfo?.formula || '[]';
        if (formulaString.includes(`{"field":"${fieldId}"`)) {
          results.push(one.title);
        }
      });
      return results;
    },
    [fields, adminFields]
  );

  const calculateVisibilityDependencies = useCallback(
    fieldId => {
      const allFields = [...fields, ...adminFields];
      let results = [];
      rules.forEach(rule => {
        const condition = rule.condition;
        if (condition.includes('${' + fieldId + '}')) {
          const matchingField = allFields.find(
            one => one.internalId === rule.fieldId
          );
          if (matchingField) {
            results.push(matchingField.title || matchingField.internalId);
          }
        }
      });
      return results;
    },
    [fields, adminFields, rules]
  );

  const calculateAutomationDependencies = useCallback(
    fieldId => {
      let results = [];
      automations.forEach(automation => {
        const condition = automation?.rule?.condition || '';
        if (condition.includes('${' + fieldId + '}')) {
          results.push(automation.label || 'untitled automation');
        }
      });
      return results;
    },
    [automations]
  );

  const calculateReminderDependencies = useCallback(
    fieldId => {
      let results = [];
      reminders.forEach(reminder => {
        const condition = reminder?.condition?.logicRules || '';
        if (condition.includes('${' + fieldId + '}')) {
          results.push(reminder.title || 'untitled reminder');
        }
      });
      return results;
    },
    [reminders]
  );

  const calculateLookupDependencies = useCallback(
    fieldId => {
      let results = [];
      const fieldsThatHaveALookup = !allFieldsWithSubFields
        ? []
        : Object.keys(allLookupDependencies);
      if (!fieldsThatHaveALookup.length) {
        return [];
      }
      if (!allLookupDependencies[fieldId]) {
        return [];
      }

      const checkOneField = _fieldId => {
        const lookupsByForm = Object.assign(
          {},
          ...(allLookupDependencies[_fieldId] || [])
        );
        const formIdsWhichLookupOnThisForm = Object.keys(lookupsByForm);
        formIdsWhichLookupOnThisForm.forEach(otherFormId => {
          const oneLookup = lookupsByForm[otherFormId];
          const formTitle = oneLookup.form_title;
          const lookupPairs = oneLookup.lookups || {};
          let foundFieldIds = [];
          let fieldTitles = [];
          const lookupPairKeys = Object.keys(lookupPairs);
          lookupPairKeys.forEach(fId => {
            foundFieldIds.push(fId);
            fieldTitles.push(lookupPairs[fId]);
          });
          results.push({
            formId: otherFormId,
            formTitle,
            fieldTitles
          });
        });
      };
      checkOneField(fieldId);
      return results;
    },
    [allLookupDependencies]
  );

  const getAllDependenciesOfTheseFields = (
    fieldsToCheck,
    isTryingToDeleteSection
  ) => {
    let allDependencyData = {};
    let newDependencyData = {};
    allDependencyData.multipleFields = fieldsToCheck.length > 1;
    let foundThisOne;
    if (isTryingToDeleteSection) {
      allDependencyData.isTryingToDeleteSection = true;
    }
    fieldsToCheck.forEach(one => {
      let oneTypeOfDependencies;
      foundThisOne = false;
      newDependencyData[one] = {};
      oneTypeOfDependencies = calculateDependentScoringFields(one);
      if (oneTypeOfDependencies.length) {
        allDependencyData.hasDependencies = true;
        allDependencyData.hasScoringDependencies = true;
        foundThisOne = true;
        newDependencyData[one].scoringFieldDependencies = oneTypeOfDependencies;
      }
      oneTypeOfDependencies = calculateVisibilityDependencies(one);
      if (oneTypeOfDependencies.length) {
        allDependencyData.hasDependencies = true;
        allDependencyData.hasVisibilityDependencies = true;
        foundThisOne = true;
        newDependencyData[one].visibilityDependencies = oneTypeOfDependencies;
      }
      oneTypeOfDependencies = calculateAutomationDependencies(one);
      if (oneTypeOfDependencies.length) {
        allDependencyData.hasDependencies = true;
        allDependencyData.hasAutomationDependencies = true;
        foundThisOne = true;
        newDependencyData[one].automationDependencies = oneTypeOfDependencies;
      }
      oneTypeOfDependencies = calculateLookupDependencies(one);
      if (oneTypeOfDependencies.length) {
        allDependencyData.hasDependencies = true;
        allDependencyData.hasLookupDependencies = true;
        foundThisOne = true;
        newDependencyData[one].lookupDependencies = oneTypeOfDependencies;
      }
      oneTypeOfDependencies = calculateReminderDependencies(one);
      if (oneTypeOfDependencies.length) {
        allDependencyData.hasDependencies = true;
        allDependencyData.hasReminderDependencies = true;
        foundThisOne = true;
        newDependencyData[one].reminderDependencies = oneTypeOfDependencies;
      }
      if (!foundThisOne) {
        delete newDependencyData[one];
      }
    });
    allDependencyData.dependenciesByField = newDependencyData;
    return allDependencyData;
  };

  const debouncedAutosaveField = useCallback(
    debounce((a, b) => {
      autosaveField(a, b);
    }, 100),
    [autosaveField]
  );

  const emojiCallback = useCallback(
    newValue => {
      if (emojiTargetRef.current) {
        autosaveField(UPDATE_FIELD, {
          field: {
            options: newValue.split('\n')
          }
        });
      } else {
        autosaveField(UPDATE_FIELD, {
          field: { title: newValue }
        });
      }
    },
    [autosaveField, options, title]
  );

  const titleChangeHandler = useCallback(
    e => {
      // title = e.target.value;
      debouncedAutosaveField(UPDATE_FIELD, {
        field: { title: e.target.value }
      });
    },
    [autosaveField]
  );

  const htmlId = `formbuilder-field-${internalId}`;
  const hasOptions = !!fieldTypes[uiType].options;

  const excludeInvalidRules = (conditions, ruleIds) => {
    let conditionsObj = JSON.parse(conditions);
    for (let i = conditionsObj.length - 1; i >= 0; i--) {
      if (
        ruleIds.some(
          one => conditionsObj[i][0] && conditionsObj[i][0].field.includes(one)
        )
      ) {
        conditionsObj.splice(i, 1);
      }
    }

    return JSON.stringify(conditionsObj);
  };

  const excludeInvalidDescriptionRules = (conditions, ruleIds) => {
    let conditionsObj = JSON.parse(conditions);
    for (let i = conditionsObj.length - 1; i >= 0; i--) {
      const singleDescriptionConditions = conditionsObj[i].conditions;
      for (let j = singleDescriptionConditions.length - 1; j >= 0; j--) {
        if (ruleIds.some(one => singleDescriptionConditions[j].input === one)) {
          singleDescriptionConditions.splice(j, 1);
        }
      }
    }

    return JSON.stringify(conditionsObj);
  };

  const addRules = useCallback(newRules => {
    newRules.forEach(one => {
      fetchAPI('/forms/v1/rules', {
        method: 'POST',
        body: one
      });
    });
  }, []);

  const onDuplicateSection = React.useCallback(
    variant => {
      switch (variant) {
        case 'header':
          duplicateField();
          break;
        case 'entire':
          const allFieldsBySection = getAllFieldsBySection(fields, adminFields);
          const sectionToDuplicate = allFieldsBySection[internalId];
          const idMapOldToNew = mapExistingFieldIdsToNewIds(
            getAllIdsOfSection(sectionToDuplicate)
          );
          const newSection = getDuplicateSection(
            sectionToDuplicate,
            idMapOldToNew
          );
          const rulesToAdd = getDuplicateRules(rules, idMapOldToNew);
          const idOfNewSection =
            idMapOldToNew[sectionToDuplicate[0].internalId];
          const newSectionIdOrder = Object.keys(allFieldsBySection);

          const indexOfFieldId = newSectionIdOrder.indexOf(internalId);
          newSectionIdOrder.splice(indexOfFieldId + 1, 0, idOfNewSection);

          allFieldsBySection[idOfNewSection] = newSection;

          const newFields = [];
          const newAdminFields = [];
          let nextFieldPosition = 0;

          newSectionIdOrder.forEach(one => {
            const fieldsOfThisSection = allFieldsBySection[one];
            let isAdminField;
            if (fieldsOfThisSection && fieldsOfThisSection.length) {
              fieldsOfThisSection.forEach(oneField => {
                isAdminField = oneField.adminField;
                delete oneField.adminField;
                oneField.position = nextFieldPosition;
                nextFieldPosition++;
                if (isAdminField) {
                  newAdminFields.push(oneField);
                } else {
                  newFields.push(oneField);
                }
              });
            }
          });

          addRules(rulesToAdd);

          autosave({
            type: UPDATE_FORM,
            payload: {
              fields: newFields,
              adminFields: newAdminFields
            },
            callback: () => {
              fetchAPI(
                `/forms/v1/rules?query=[[{"field":"'formId'","op":"=","value":"${formId}"}]]`
              ).then(res => {
                setRules(res.results);
              });
            }
          });
          break;
      }
      setShowDuplicateSectionDialog(false);
    },
    [fields, adminFields, internalId, rules]
  );

  const onClickedDuplicateSection = React.useCallback(() => {
    setShowDuplicateSectionDialog(true);
  }, []);

  const onClickedDuplicateWithChildren = React.useCallback(() => {
    const sourceField = allFieldsWithSubFields.find(
      one => one.internalId === internalId
    );
    const positionOfCurrentField = sourceField.position;
    const subfields = allFieldsWithSubFields.filter(
      one => one?.lookupInfo?.subFieldInfo?.parentFieldId === internalId
    );
    const positionOfLastSubfield = positionOfCurrentField + subfields.length;
    const unaffectedFields = allFieldsWithSubFields.filter(
      one => one.position <= positionOfLastSubfield
    );
    const fieldsAfterSourceFieldsChildren = allFieldsWithSubFields.filter(
      one =>
        one.position > positionOfCurrentField &&
        (!one.lookupInfo ||
          !one.lookupInfo.subFieldInfo ||
          one.lookupInfo.subFieldInfo.parentFieldId !== internalId)
    );
    const fieldsToDuplicate = [sourceField, ...subfields];
    const idMapOldToNew = mapExistingFieldIdsToNewIds(
      getAllIdsOfSection(fieldsToDuplicate)
    );
    const duplicatedFields = getDuplicateSection(
      fieldsToDuplicate,
      idMapOldToNew
    );
    const rulesToAdd = getDuplicateRules(rules, idMapOldToNew);
    const newFields = [];
    const newAdminFields = [];
    let nextFieldPosition = 0;
    let isAdminField;
    unaffectedFields.forEach(one => {
      isAdminField = one.isAdminOnly;
      delete one.isAdminOnly;
      one.position = nextFieldPosition;
      nextFieldPosition++;
      if (isAdminField) {
        newAdminFields.push(one);
      } else {
        newFields.push(one);
      }
    });
    duplicatedFields.forEach(one => {
      isAdminField = one.isAdminOnly;
      delete one.isAdminOnly;
      one.position = nextFieldPosition;
      nextFieldPosition++;
      if (isAdminField) {
        newAdminFields.push(one);
      } else {
        newFields.push(one);
      }
    });
    fieldsAfterSourceFieldsChildren.forEach(one => {
      isAdminField = one.isAdminOnly;
      delete one.isAdminOnly;
      one.position = nextFieldPosition;
      nextFieldPosition++;
      if (isAdminField) {
        newAdminFields.push(one);
      } else {
        newFields.push(one);
      }
    });
    addRules(rulesToAdd);

    autosave({
      type: UPDATE_FORM,
      payload: {
        fields: newFields,
        adminFields: newAdminFields
      },
      callback: () => {
        fetchAPI(
          `/forms/v1/rules?query=[[{"field":"'formId'","op":"=","value":"${formId}"}]]`
        ).then(res => {
          setRules(res.results);
        });
      }
    });
  }, [allFieldsWithSubFields, fields, adminFields, internalId, rules]);

  const duplicateField = React.useCallback(() => {
    const newFieldId = 'f' + generateId();
    autosaveField(DUPLICATE_FIELD, { internalId: newFieldId });

    const currentFieldRules = JSON.parse(
      JSON.stringify(rules.filter(rule => rule.fieldId === internalId))
    );
    if (currentFieldRules.length) {
      currentFieldRules.forEach(rule => {
        rule.fieldId = newFieldId;
        delete rule.id;
        delete rule.metadata;

        fetchAPI('/forms/v1/rules', {
          method: 'POST',
          body: rule,
          handleResponse: r =>
            r
              .text()
              .then(text => (text ? JSON.parse(text) : {}))
              .then(json => {
                if (r.ok) {
                  return r;
                } else {
                  return Promise.reject({
                    response: r,
                    json
                  });
                }
              })
        }).then(r => {
          // get ID from Location header
          const id = r.headers.map.location;
          rule.id = id;
        });
      });

      const newRules = [...rules, ...currentFieldRules];
      setRules(newRules);
    }
  }, [allFieldsWithSubFields, rules, setRules]);

  const deleteFields = React.useCallback(
    (idsToDelete = [internalId]) => {
      autosave({
        type: DELETE_FIELDS,
        payload: {
          fieldIds: idsToDelete
        }
      });
      let rules2DeleteFromState = [];
      // delete rules on server
      rules.forEach(r => {
        if (idsToDelete.indexOf(r.fieldId) !== -1) {
          rules2DeleteFromState.push(r.id);
          fetchAPI(`/forms/v1/rules/${r.id}`, { method: 'DELETE' });
        }
      });

      // delete rules in state
      rules.forEach(rule => {
        if (idsToDelete.some(one => rule.condition.includes(one))) {
          if (rule.action.actionType === 'description') {
            rule.condition = excludeInvalidDescriptionRules(
              rule.condition,
              idsToDelete
            );
          } else {
            rule.condition = excludeInvalidRules(rule.condition, idsToDelete);
          }
          if (!JSON.parse(rule.condition).length) {
            if (!rules2DeleteFromState.includes(rule.id)) {
              rules2DeleteFromState.push(rule.id);
              fetchAPI(`/forms/v1/rules/${rule.id}`, { method: 'DELETE' });
            }
          } else {
            fetchAPI(`/forms/v1/rules/${rule.id}`, {
              method: 'PUT',
              body: rule
            });
          }
        }
      });

      let newRules = rules.filter(r => !rules2DeleteFromState.includes(r.id));

      setRules(newRules);
      setDeletedField(idsToDelete[0]);
    },
    [
      autosaveField,
      allFieldsWithSubFields,
      internalId,
      rules,
      setRules,
      fetchAPI
    ]
  );

  const deleteSection = React.useCallback(() => {
    // delete the fields
    const idsToDelete = getAllFieldIdsInSection(internalId);
    let subFieldsToDelete = [];
    subFieldsToDelete = allFieldsWithSubFields.filter(
      one =>
        one.lookupInfo &&
        one.lookupInfo.subFieldInfo &&
        idsToDelete.includes(one.lookupInfo.subFieldInfo.parentFieldId)
    );
    return deleteFields([
      ...idsToDelete,
      ...subFieldsToDelete.map(one => one.internalId)
    ]);
  }, [
    autosaveField,
    allFieldsWithSubFields,
    internalId,
    rules,
    setRules,
    fetchAPI
  ]);

  const deleteField = React.useCallback(() => {
    // delete the field
    let subFieldsToDelete = [];

    if (lookupInfo && lookupInfo.subFields) {
      subFieldsToDelete = allFieldsWithSubFields.filter(
        one =>
          one.lookupInfo &&
          one.lookupInfo.subFieldInfo &&
          one.lookupInfo.subFieldInfo.parentFieldId === internalId
      );
      return deleteFields([
        internalId,
        ...subFieldsToDelete.map(one => one.internalId)
      ]);
    }

    autosaveField(DELETE_FIELD);

    let rules2DeleteFromState = [];

    // delete rules on server
    rules.forEach(r => {
      if (r.fieldId === internalId) {
        rules2DeleteFromState.push(r.id);
        fetchAPI(`/forms/v1/rules/${r.id}`, { method: 'DELETE' });
      }
    });

    // delete rules in state
    rules.forEach(rule => {
      if (rule.condition.includes(internalId)) {
        if (rule.action.actionType === 'description') {
          rule.condition = excludeInvalidDescriptionRules(rule.condition, [
            internalId
          ]);
        } else {
          rule.condition = excludeInvalidRules(rule.condition, [internalId]);
        }
        if (!JSON.parse(rule.condition).length) {
          if (!rules2DeleteFromState.includes(rule.id)) {
            rules2DeleteFromState.push(rule.id);
            fetchAPI(`/forms/v1/rules/${rule.id}`, { method: 'DELETE' });
          }
        } else {
          fetchAPI(`/forms/v1/rules/${rule.id}`, {
            method: 'PUT',
            body: rule
          });
        }
      }
    });

    let newRules = rules.filter(r => !rules2DeleteFromState.includes(r.id));

    setRules(newRules);
    setDeletedField(internalId);
  }, [
    autosaveField,
    allFieldsWithSubFields,
    internalId,
    rules,
    setRules,
    fetchAPI
  ]);

  const disableAllTypeChanges =
    (hasEntries && compatibleTypesMap[uiType].length === 0) ||
    neverChangeTypes.includes(uiType);

  // When mounting a field, if it has just been added by Redux, we animate its height up from zero, animate the translateY of the following fields, and focus the title
  React.useEffect(() => {
    if (archived) {
      return;
    }
    if (window.formBuilderNewestField === internalId) {
      if (
        window.formBuilderNewestFieldUIType === 'lookup' ||
        window.formBuilderNewestFieldUIType === 'user_lookup'
      ) {
        setShowLookupDialog(true);
      }

      if (window.formBuilderNewestFieldUIType === 'currency_conversion') {
        setShowCurrencyConversionDialog(true);
      }

      if (window.formBuilderNewestFieldUIType === 'score') {
        setShowScoreDialog(true);
      }

      const row = document.getElementById(`react-virtualized-row-${index}`);
      const following = document.querySelectorAll(
        `#react-virtualized-row-${index} ~ div`
      );
      const height = parseInt(row.style.height);

      if (isNaN(height)) {
        window.formBuilderNewestField = null;
        window.formBuilderNewestFieldUIType = null;
        return;
      }

      if (titleRef.current) {
        // if (titleRef.current) {
        titleRef.current.focus();
        titleRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }

      window.TweenLite.fromTo(row, 1, { height: 0 }, { height });

      window.TweenLite.fromTo(
        following,
        1,
        { y: -height },
        {
          y: 0,
          delay: 0.1,
          onComplete: () => {
            window.formBuilderNewestField = null;
            window.formBuilderNewestFieldUIType = null;
          }
        }
      );
    }
  }, []);

  // When deleting or archiving, animate the height to zero and animate the translateY of the following fields
  React.useEffect(() => {
    if (archived) {
      return;
    }
    if (archiveMe || deleteMe) {
      const row = document.getElementById(`react-virtualized-row-${index}`);
      const following = document.querySelectorAll(
        `#react-virtualized-row-${index} ~ div`
      );
      const height = parseInt(row.style.height);

      window.TweenLite.fromTo(row, 0.2, { height }, { height: 0 });

      window.TweenLite.fromTo(
        following,
        0.2,
        { y: 0 },
        {
          y: -height,
          onComplete: () => {
            if (archiveMe) {
              archiveField();
            }
            if (deleteMe === 'section') {
              deleteSection();
            } else if (deleteMe) {
              deleteField();
            }
          }
        }
      );
    }
  }, [archiveMe, deleteMe]);

  if (archived) {
    return '';
  }

  const contextProps = { field };
  const filteredFieldTypes = cloneDeep(fieldTypes);

  if (sharingType === 'anonymous') {
    delete filteredFieldTypes.user_lookup;
    delete filteredFieldTypes.thirdfort;
    delete filteredFieldTypes.worldcheck;
    delete filteredFieldTypes.worldcheck_org;
    delete filteredFieldTypes.credas;
    delete filteredFieldTypes.credas_out_of_scope;
  }

  if (!activeIntegrations || !activeIntegrations.thirdfort) {
    delete filteredFieldTypes.thirdfort;
  }
  if (!activeIntegrations || !activeIntegrations.worldcheck) {
    delete filteredFieldTypes.worldcheck;
    delete filteredFieldTypes.worldcheck_org;
  }
  if (!activeIntegrations || !activeIntegrations.credas) {
    delete filteredFieldTypes.credas;
    delete filteredFieldTypes.credas_out_of_scope;
  }

  if (!activeLicenses || !activeLicenses.currencyconversion) {
    delete filteredFieldTypes.currency_conversion;
  }

  const checkboxVisible = isHovered || anyFieldSelected;

  return (
    <FormBuilderFieldFormContext.Provider value={contextProps}>
      <>
        <div
          className={classNames({
            FBFF__HoverTarget: true,
            'FBFF__HoverTarget--isDragging': snapshot.isDragging,
            'FBFF__HoverTarget--isHovered': isHovered,
            // active while dragging
            'FBFF__HoverTarget--isActive':
              snapshot.isDragging ||
              // or has focus or hovered, but not during another field's drag
              (hasFocus && !snapshot.draggingOver)
          })}
          onMouseOver={() => {
            if (!window.isDragging) {
              setIsHovered(true);
              setOnceHovered(true);
            }
          }}
          onMouseOut={() => setIsHovered(false)}
          // {...provided.draggableProps}
          // ref={provided.innerRef}
        >
          <div
            className="FBFF__Container"
            id={htmlId}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            data-testid={htmlId}
            data-fieldtype={uiType}
          >
            <FieldCheckboxContainer visible={checkboxVisible}>
              <StyledCheckbox
                color="primary"
                checked={multiSelected[htmlId]}
                key={htmlId + '-checkbox-' + multiSelected[htmlId]}
                onChange={e => {
                  changeMultiSelected(htmlId, e.target.checked);
                }}
              />
            </FieldCheckboxContainer>
            <div className="FBFF__DragHandle" {...provided.dragHandleProps} />
            <div className="FBFF__DragHandleMask" />
            <div className="inUseByRule">
              {inUseInVisibilityLogic && (
                <Tooltip title="This field is being used in conditional logic.">
                  <VisibilityIcon />
                </Tooltip>
              )}
              {inUseInFlaggingLogic && (
                <Tooltip title="This field is being used in flag logic.">
                  <FlagIcon />
                </Tooltip>
              )}
            </div>
            {!isHovered && !fieldCornerIconMenuOpen ? (
              <FieldTypeIcon
                fieldType={uiType}
                css={css`
                  position: absolute;
                  top: 16px;
                  left: 16px;
                  margin: 0;
                `}
              />
            ) : (
              <FieldCornerIconSelect
                value={uiType}
                onOpen={() => setFieldCornerIconMenuOpen(true)}
                onClose={() => setFieldCornerIconMenuOpen(false)}
                data-testid={`${htmlId}-select-type`}
                disabled={disableAllTypeChanges}
                onChange={e => {
                  const ut = e.target.value;
                  // could be undefined if helper text is clicked
                  if (!ut) return;

                  const currentFieldTypeHasOptions = fieldTypes[uiType].options;
                  const newFieldsTypeHasOptions = fieldTypes[ut].options;
                  const cantBeRequired = fieldTypes[ut].cantBeRequired;
                  const field = {
                    uiType: ut,
                    type: fieldTypes[ut].type
                  };

                  if (required && cantBeRequired) {
                    field.required = false;
                  }

                  if (currentFieldTypeHasOptions && !newFieldsTypeHasOptions) {
                    field.options = [];
                  }

                  autosaveField(UPDATE_FIELD, {
                    field
                  });
                }}
              >
                {Object.keys(filteredFieldTypes).map(ft => {
                  if (fieldTypes[ft].dontList) {
                    return null;
                  }
                  const disabled =
                    // invalid because of rule
                    // (
                    // inUseByRule &&
                    // compareTypeMap[ft] !== compareTypeMap[uiType]) ||
                    // invalid because of entries
                    (hasEntries && !compatibleTypesMap[uiType].includes(ft)) ||
                    (ft === 'user_lookup' && sharingType === 'anonymous');

                  return (
                    <StyledFieldTypeMenuItem
                      data-testid={`${htmlId}-select-type-option-${ft}`}
                      key={ft}
                      value={ft}
                      disabled={disabled}
                    >
                      <FieldTypeIcon
                        fieldType={ft}
                        disabled={fieldCornerIconMenuOpen && disabled}
                        style={{
                          margin: 0
                        }}
                      />
                      <span className="StyledFieldTypeMenuItem__name">
                        <TextString content={fieldTypes[ft].name} />
                      </span>
                    </StyledFieldTypeMenuItem>
                  );
                })}
              </FieldCornerIconSelect>
            )}
            <Input
              multiline
              defaultValue={title || ''}
              placeholder={
                uiType === 'instructional_text'
                  ? 'Write instructional text here'
                  : uiType === 'section_break'
                  ? 'Write your section header here'
                  : 'Write your question here'
              }
              isH2={uiType === 'section_break'}
              inputRef={titleRef}
              inputProps={{
                maxLength: uiType === 'instructional_text' ? null : 500,
                style: {
                  fontWeight: uiType === 'instructional_text' ? null : 599
                },
                'aria-label': 'Field title',
                'data-testid': `${htmlId}-title-input`,
                onChange: titleChangeHandler,
                className: 'FBFF__title',
                onFocus: e => {
                  emojiTargetRef.current = null;
                  setHasFocus(true);
                }
              }}
            />
            {uiType !== 'instructional_text' && (
              <CharacterCounterDiv>
                <Animation in={hasFocus}>
                  <CharacterCounter
                    string={title}
                    maxLength={500}
                    align="left"
                  />
                </Animation>
              </CharacterCounterDiv>
            )}
            {/* {description} */}
            <Collapse in={hasOptions}>
              {hasOptions && (
                <OptionsBuilder
                  htmlId={internalId}
                  singleOption={uiType === 'confirmation'}
                  disabled={inUseByRule}
                  options={options || []}
                  emojiTargetRef={emojiTargetRef}
                  dontUpdateValue={dontUpdateValue}
                  onChange={o => {
                    calculateRelatedRules();
                    let scoringMap = updateScoringMap(
                      o,
                      field.calculationMapping
                    );
                    let warningDisplayedList = warningDisplayedFor;
                    resizeFromItem(index);
                    if (
                      inUseByRule &&
                      !warningDisplayedList.includes(internalId) &&
                      uiType !== 'confirmation'
                    ) {
                      setShowRemoveOptionDialog(true);
                      warningDisplayedList.push(internalId);
                      setWarningDisplayedFor(warningDisplayedList);
                      setDontUpdateValue(true);
                      return true;
                    }
                    throttledAutoSave({
                      options: o,
                      calculationMapping: scoringMap
                    });
                  }}
                />
              )}
            </Collapse>
            {isDrawerMounted && (
              <FieldOptionsDrawer
                anchor="right"
                open={isSettingDrawerOpen}
                onClose={() => setIsSettingDrawerOpen(false)}
                defaultTab={fieldSettingsTab}
                {...{
                  htmlId,
                  formId,
                  internalId,
                  title,
                  options,
                  defaultCountry,
                  rules,
                  setRules,
                  uiType,
                  description,
                  setDescription,
                  deletedField,
                  archived,
                  autosave,
                  autosaveField,
                  autosaveSubfield,
                  isRequired: !!required,
                  isAdminOnly,
                  isReadOnly: readOnly,
                  isAdminOnlyOptionDisabled: hasEntries && !isNew
                }}
              />
            )}
            <Actions
              {...{
                canEdit,
                htmlId,
                autosaveField,
                inUseByRule,
                calculateDependentScoringFields,
                calculateVisibilityDependencies,
                calculateAutomationDependencies,
                calculateLookupDependencies,
                calculateReminderDependencies,
                getAllFieldIdsInSection,
                getAllDependenciesOfTheseFields,
                setShowEmojiDialog,
                setShowArchiveDialog,
                setShowDeleteDialog,
                setShowDeleteSectionDialog,
                setShowCantDeleteFieldDialog,
                setDependencyData,
                setFieldIdThatCantBeDeleted,
                setWarnDeletingFieldUsedInReport,
                setShowCurrencyConversionDialog,
                setShowLookupDialog,
                setShowScoreDialog,
                setFieldSettingsTab,
                setIsSettingDrawerOpen,
                isHovered,
                onClickedDuplicateSection,
                onClickedDuplicateWithChildren,
                duplicateField,
                uiType,
                fetchAPI,
                formId,
                internalId,
                thisFieldsSubFieldIds,
                calculateRelatedRules,
                setShowRemoveOptionDialog,
                changeMultiSelected
              }}
            />
          </div>
          {onceHovered && !isLast && (
            <AddQuestionButton
              compact
              after={position}
              autosave={autosave}
              ButtonComponent={InlineButton}
              color="primary"
              className="FBFF__add"
              data-testid={`formbuilder-add-new-question-${internalId}`}
              sharingType={sharingType}
              activeIntegrations={activeIntegrations}
              activeLicenses={activeLicenses}
            />
          )}
        </div>

        <EmojiInserterEngine
          emojiCallback={emojiCallback}
          textInput={titleRef.current}
          emojiTargetRef={emojiTargetRef}
          showEmojiDialog={showEmojiDialog}
          setShowEmojiDialog={setShowEmojiDialog}
        />

        <GeneralDialog
          open={showArchiveDialog}
          title={
            inUseByRule
              ? 'form-builder.archive-field.warning'
              : 'form-builder.archive-field.confirmation1'
          }
          content={
            inUseByRule
              ? t('form-builder.archive-field.confirmation.logic', {
                  fieldName: title
                })
              : 'form-builder.archive-field.confirmation2'
          }
          handleClose={() => setShowArchiveDialog(false)}
          handleConfirm={() => {
            setArchiveMe(true);
            setShowArchiveDialog(false);
          }}
        />

        <GeneralDialog
          open={showDuplicateSectionDialog}
          title="form-builder.duplicate-section.title"
          content="form-builder.duplicate-section.body"
          buttons={[
            {
              text: 'form-builder.duplicate-section.header-only',
              onClick: 'confirm'
            },
            {
              text: 'form-builder.duplicate-section.entire-section',
              variant: 'outlined',
              onClick: () => {
                onDuplicateSection('entire');
              }
            }
          ]}
          handleClose={() => setShowDuplicateSectionDialog(false)}
          handleConfirm={() => {
            onDuplicateSection('header');
          }}
        />

        {showLookupDialog ? (
          <LookupSettingsPanelEngine
            autosave={autosave}
            autosaveField={autosaveField}
            formId={formId}
            internalId={internalId}
            isAdminOnly={isAdminOnly}
            lookupInfo={lookupInfo}
            options={options || []}
            showLookupDialog={showLookupDialog}
            setShowLookupDialog={setShowLookupDialog}
            entityType={uiType === 'user_lookup' ? 'user' : 'form'}
          />
        ) : (
          ''
        )}

        {showCurrencyConversionDialog ? (
          <CurrencyConversionSettingsPanelEngine
            autosaveField={autosaveField}
            formId={formId}
            internalId={internalId}
            isAdminOnly={isAdminOnly}
            options={options || []}
            showCurrencyConversionDialog={showCurrencyConversionDialog}
            setShowCurrencyConversionDialog={setShowCurrencyConversionDialog}
          />
        ) : (
          ''
        )}

        {showScoreDialog ? (
          <ScoreSettingsPanelEngine
            autosave={autosave}
            autosaveField={autosaveField}
            formId={formId}
            internalId={internalId}
            isAdminOnly={isAdminOnly}
            showScoreDialog={showScoreDialog}
            setShowScoreDialog={setShowScoreDialog}
            allFields={allFieldsWithSubFields}
          />
        ) : (
          ''
        )}

        <CantDeleteFieldDialogEngine
          allFields={allFieldsWithSubFields}
          showCantDeleteFieldDialog={showCantDeleteFieldDialog}
          fieldIdThatCantBeDeleted={fieldIdThatCantBeDeleted}
          dependencyData={dependencyData}
          setShowCantDeleteFieldDialog={setShowCantDeleteFieldDialog}
          clickedDeleteAnyway={() => {
            setDeleteMe(showCantDeleteFieldDialog); // can be 'section' for a section, or true for a field
            setShowCantDeleteFieldDialog(false);
          }}
        />

        <GeneralDialog
          variant="delete"
          open={showDeleteDialog}
          title={
            'form-builder.field.delete-dialog.title' +
            (inUseByRule || inUseByIntegration ? '.warning' : '')
          }
          handleClose={() => setShowDeleteDialog(false)}
          handleConfirm={() => {
            setDeleteMe(true);
            setShowDeleteDialog(false);
          }}
        >
          {inUseByIntegration ? (
            <p>
              {`This field ' +
                  ${title} +
                  ' is being used in ' +
                  ${inUseByIntegration}`}
            </p>
          ) : inUseByRule ? (
            <p>
              {t('form-builder.field.delete-dialog.content.logic', {
                fieldName: title
              })}
              <ul>
                {rulesToDelete.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
              {t('form-builder.field.delete-dialog.content.logic2')}
            </p>
          ) : warnDeletingFieldUsedInReport ? (
            <p>
              {t('form-builder.field.delete-dialog.content.report', {
                fieldName: title
              })}
            </p>
          ) : (
            <p>
              {t('form-builder.field.delete-dialog.content', {
                fieldName: title
              })}
            </p>
          )}
        </GeneralDialog>
        <RemoveOptionDialog
          variant="feature"
          open={ShowRemoveOptionDialog}
          title={
            'form-builder.field.delete-dialog.title' +
            (inUseByRule || inUseByIntegration ? '.warning' : '')
          }
          handleClose={() => {
            setShowRemoveOptionDialog(false);
            setDontUpdateValue(false);
          }}
          handleConfirm={() => {
            setShowRemoveOptionDialog(false);
            setDontUpdateValue(false);
          }}
        >
          {inUseByIntegration ? (
            <p>
              {`This field ' +
                  ${title} +
                  ' is being used in ' +
                  ${inUseByIntegration}`}
            </p>
          ) : inUseByRule ? (
            <p>
              {t('form-builder.field.delete-dialog.content.logic', {
                fieldName: title
              })}
              <ul>
                {rulesToDelete.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
              {t('form-builder.field.remove-value-dialog.content.logic2')}
              {/* <br />
              {t('form-builder.field.remove-value-dialog.content.footer')} */}
            </p>
          ) : warnDeletingFieldUsedInReport ? (
            <p>
              {t('form-builder.field.delete-dialog.content.report', {
                fieldName: title
              })}
            </p>
          ) : (
            <p>
              {t('form-builder.field.delete-dialog.content', {
                fieldName: title
              })}
            </p>
          )}
        </RemoveOptionDialog>
        <GeneralDialog
          variant="delete"
          open={showDeleteSectionDialog}
          title="form-builder.section.delete-dialog.title"
          handleClose={() => setShowDeleteSectionDialog(false)}
          handleConfirm={() => {}}
          buttons={[
            {
              text: 'form-builder.section.delete-dialog.header-only',
              onClick: () => {
                setDeleteMe(true);
                setShowDeleteSectionDialog(false);
              }
            },
            {
              text: 'form-builder.section.delete-dialog.entire-section',
              variant: 'outlined',
              onClick: () => {
                const fieldsInSection =
                  uiType === 'section_break'
                    ? getAllFieldIdsInSection(internalId)
                    : null;
                const newDependencyData = getAllDependenciesOfTheseFields(
                  fieldsInSection,
                  true
                );
                setDependencyData(newDependencyData);
                if (newDependencyData.hasDependencies) {
                  setShowCantDeleteFieldDialog(
                    fieldsInSection ? 'section' : true
                  );
                  setFieldIdThatCantBeDeleted(internalId);
                } else {
                  setShowDeleteSectionConfirmation(true);
                  setShowDeleteSectionDialog(false);
                }
              }
            }
          ]}
        >
          <p>{t('form-builder.section.delete-dialog.body')}</p>
        </GeneralDialog>
        <GeneralDialog
          variant="delete"
          open={showDeleteSectionConfirmation}
          title="form-builder.section.delete-confirmation.title.warning"
          handleClose={() => setShowDeleteSectionConfirmation(false)}
          handleConfirm={() => {
            setDeleteMe('section');
            setShowDeleteSectionConfirmation(false);
          }}
        >
          <p>
            {t('form-builder.section.delete-confirmation.content', {
              sectionName: title
            })}
          </p>
        </GeneralDialog>
      </>
    </FormBuilderFieldFormContext.Provider>
  );
});

const Actions = React.memo(
  ({
    canEdit,
    getAllFieldIdsInSection,
    htmlId,
    autosaveField, // inUseByRule,
    setShowArchiveDialog,
    setShowDeleteDialog,
    setShowDeleteSectionDialog,
    setShowCantDeleteFieldDialog,
    setDependencyData,
    getAllDependenciesOfTheseFields,
    setFieldIdThatCantBeDeleted,
    setWarnDeletingFieldUsedInReport,
    setShowEmojiDialog,
    setShowCurrencyConversionDialog,
    setShowLookupDialog,
    setShowScoreDialog,
    setFieldSettingsTab,
    setIsSettingDrawerOpen,
    isHovered,
    onClickedDuplicateSection,
    onClickedDuplicateWithChildren,
    duplicateField,
    uiType,
    fetchAPI,
    formId,
    internalId,
    thisFieldsSubFieldIds,
    calculateRelatedRules,
    changeMultiSelected
  }) => {
    const t = useTranslations();

    return (
      <Fade in={isHovered} mountOnEnter>
        <div className="FBFF__ActionRow">
          <ActionButton
            label="emoji"
            icon="☺"
            onClick={() => {
              setShowEmojiDialog(true);
            }}
            size="small"
            data-testid={`${htmlId}-emoji-button`}
          />
          <ActionButton
            label="Visibility"
            icon={<VisibilityIcon />}
            onClick={() => {
              setFieldSettingsTab('visibility');
              setIsSettingDrawerOpen(true);
            }}
            data-testid={`${htmlId}-visibility-button`}
          />
          {uiType !== 'lookup' &&
          uiType !== 'user_lookup' &&
          uiType !== 'thirdfort' &&
          uiType !== 'worldcheck' &&
          uiType !== 'credas' &&
          uiType !== 'credas_out_of_scope' &&
          uiType !== 'worldcheck_org' ? (
            <>
              <ActionButton
                label="Duplicate"
                icon={<DuplicateIcon />}
                onClick={
                  uiType === 'section_break'
                    ? onClickedDuplicateSection
                    : uiType === 'currency_conversion'
                    ? onClickedDuplicateWithChildren
                    : duplicateField
                }
                data-testid={`${htmlId}-duplicate-button`}
              />
              <ActionButton
                label={'Archive'}
                icon={<ArchiveIcon />}
                onClick={() => {
                  changeMultiSelected(-1);
                  setShowArchiveDialog(true);
                }}
                data-testid={`${htmlId}-archive-button`}
              />
            </>
          ) : (
            ''
          )}
          {(uiType === 'lookup' || uiType === 'user_lookup') && canEdit ? (
            <ActionButton
              label={t('forms.field_type.lookup_edit')}
              icon={<EditIcon />}
              onClick={() => setShowLookupDialog(true)}
              data-testid={`${htmlId}-lookup-button`}
            />
          ) : (
            ''
          )}
          {uiType === 'currency_conversion' && canEdit ? (
            <ActionButton
              label={t('forms.field_type.currency_conversion_edit')}
              icon={<EditIcon />}
              onClick={() => setShowCurrencyConversionDialog(true)}
              data-testid={`${htmlId}-currency-conversion-button`}
            />
          ) : (
            ''
          )}
          {uiType === 'score' && canEdit ? (
            <ActionButton
              label={t('forms.field_type.score_edit')}
              icon={<EditIcon />}
              onClick={() => setShowScoreDialog(true)}
              data-testid={`${htmlId}-score-button`}
            />
          ) : (
            ''
          )}
          <ActionButton
            label={'Delete'}
            icon={<DeleteIcon />}
            data-testid={`${htmlId}-delete-button`}
            onClick={() => {
              fetchAPI(
                `/forms/v1/reports?query=[[{"field": "'formId'", "op": "=", "value": "${formId}"}]]`
              ).then(res => {
                let str, foundThisField;
                foundThisField = false;
                if (res && res.results && res.results.length) {
                  res.results.forEach(one => {
                    str = JSON.stringify(one.children || '');
                    if (str.indexOf(`"fieldId":"${internalId}"`) !== -1) {
                      foundThisField = true;
                    }
                  });
                }
                setWarnDeletingFieldUsedInReport(foundThisField);
                changeMultiSelected(-1);
                if (uiType === 'section_break') {
                  setShowDeleteSectionDialog(true);
                  return;
                }
                let fieldsToCheck = [internalId, ...thisFieldsSubFieldIds];
                // add subfields if they exist
                const newDependencyData =
                  getAllDependenciesOfTheseFields(fieldsToCheck);
                setDependencyData(newDependencyData);
                if (newDependencyData.hasDependencies) {
                  setShowCantDeleteFieldDialog(true);
                  setFieldIdThatCantBeDeleted(internalId);
                } else {
                  setShowDeleteDialog(true);
                  calculateRelatedRules();
                }
              });
            }}
          />

          <ActionButton
            label="Settings"
            icon={<SettingsIcon />}
            onClick={() => {
              setFieldSettingsTab(null);
              setIsSettingDrawerOpen(true);
            }}
            data-testid={`${htmlId}-settings-button`}
          />
        </div>
      </Fade>
    );
  }
);
